import React, { useState, useContext, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { SocketContext } from '../../App';
import styles from './GameJoinPage.module.css';
import toastManager from '../../utils/toastManager';
import PasswordField from '../UI/PasswordField';
import { backendUrl } from '../../config/api';

const GameJoinPage = () => {
    const { roomId } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const socket = useContext(SocketContext);
    const [name, setName] = useState('');
    const [password, setPassword] = useState('');
    const [mobile, setMobile] = useState('');
    const [email, setEmail] = useState('');
    const [joinToken, setJoinToken] = useState('');
    const [slotLoading, setSlotLoading] = useState(false);
    const [roomMeta, setRoomMeta] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [blockingSession, setBlockingSession] = useState(null);
    // When name comes from URL (set by room creator), lock it — no editing
    const [nameLocked, setNameLocked] = useState(false);

    const sessionKey = roomId ? `ludo_session_${roomId}` : null;
    const autoJoinFiredRef = useRef(false);
    const joinFallbackTimerRef = useRef(null);

    const clearStaleSessionState = (targetRoomId) => {
        try {
            const activeSessionRaw = localStorage.getItem('ludoActiveSession');
            const activeSession = activeSessionRaw ? JSON.parse(activeSessionRaw) : null;
            if (activeSession?.status === 'completed') {
                localStorage.removeItem('ludoActiveSession');
            }
        } catch (_) {}
        try {
            const lastResultRaw = localStorage.getItem('ludoLastGameResult');
            const lastResult = lastResultRaw ? JSON.parse(lastResultRaw) : null;
            if (!lastResult || lastResult.roomId !== targetRoomId) {
                localStorage.removeItem('ludoLastGameResult');
            }
        } catch (_) {}
    };

    // ── Emit join ──────────────────────────────────────────────────────────
    const emitJoin = (joinName, joinPass, token, mob, eml) => {
        clearStaleSessionState(roomId);
        localStorage.setItem('playerName', joinName);
        try {
            const saved = localStorage.getItem(sessionKey);
            const session = saved ? JSON.parse(saved) : {};
            localStorage.setItem(sessionKey, JSON.stringify({
                ...session,
                name: joinName,
                token: token || session.token || '',
                password: joinPass || '',
                mobile: mob || '',
                email: eml || '',
            }));
        } catch (_) {}
        setLoading(true);
        setError(null);
        socket.emit('player:login', {
            name:     joinName,
            roomId,
            password: joinPass || undefined,
            token:    token    || undefined,
            mobile:   mob      || undefined,
            email:    eml      || undefined,
        });

        if (joinFallbackTimerRef.current) clearTimeout(joinFallbackTimerRef.current);
        joinFallbackTimerRef.current = setTimeout(() => {
            socket.emit('room:join', { roomId, playerName: joinName, password: joinPass });
        }, 2500);
    };

    useEffect(() => {
        clearStaleSessionState(roomId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId]);

    useEffect(() => {
        if (!socket || !roomId) return;

        // Socket listeners
        const handlePlayerData = () => {
            if (joinFallbackTimerRef.current) clearTimeout(joinFallbackTimerRef.current);
            setLoading(false);
            navigate('/play');
        };
        const handleEncKey = key => {
            if (!key || !sessionKey) return;
            try {
                const saved = localStorage.getItem(sessionKey);
                const session = saved ? JSON.parse(saved) : {};
                localStorage.setItem(sessionKey, JSON.stringify({ ...session, encKey: key }));
                localStorage.setItem(`ludo_enc_${roomId}`, key);
            } catch (_) {}
        };
        const handleError = err => {
            if (joinFallbackTimerRef.current) clearTimeout(joinFallbackTimerRef.current);
            autoJoinFiredRef.current = false;
            const msg = err.message || 'An error occurred';
            setError(msg);
            toastManager.info(msg);
            setLoading(false);
        };
        const handleWrongPassword = () => {
            if (joinFallbackTimerRef.current) clearTimeout(joinFallbackTimerRef.current);
            autoJoinFiredRef.current = false;
            setError('Incorrect password');
            toastManager.info('Incorrect password');
            setLoading(false);
        };
        const handleAlreadyLoggedIn = () => {
            if (joinFallbackTimerRef.current) clearTimeout(joinFallbackTimerRef.current);
            autoJoinFiredRef.current = false;
            setError('Already logged in to another game. Please refresh and try again.');
            toastManager.info('Already logged in to another game.');
            setLoading(false);
        };
        const handleRoomJoinResponse = (response) => {
            try {
                const r = typeof response === 'string' ? JSON.parse(response) : response;
                if (r.status === 'ok') {
                    if (joinFallbackTimerRef.current) clearTimeout(joinFallbackTimerRef.current);
                    setLoading(false);
                    navigate('/play');
                    return;
                }
                if (r.status !== 'ok') {
                    if (joinFallbackTimerRef.current) clearTimeout(joinFallbackTimerRef.current);
                    autoJoinFiredRef.current = false;
                    toastManager.info(r.message || 'Failed to join game');
                    setError(r.message || 'Failed to join game');
                    setLoading(false);
                }
            } catch (_) {}
        };
        const handleActiveSession = (sessionData) => {
            if (!sessionData?.roomId || !sessionData?.playerId) {
                setBlockingSession(null);
                return;
            }
            if (sessionData.status === 'completed') {
                setBlockingSession(null);
                return;
            }
            if (sessionData.roomId !== roomId) {
                setBlockingSession(sessionData);
            } else {
                setBlockingSession(null);
            }
        };

        socket.on('player:data',           handlePlayerData);
        socket.on('player:encKey',         handleEncKey);
        socket.on('error',                 handleError);
        socket.on('error:wrongPassword',   handleWrongPassword);
        socket.on('error:alreadyLoggedIn', handleAlreadyLoggedIn);
        socket.on('room:join:response',    handleRoomJoinResponse);
        socket.on('player:active-session', handleActiveSession);
        socket.emit('player:check-session');

        return () => {
            socket.off('player:data',           handlePlayerData);
            socket.off('player:encKey',         handleEncKey);
            socket.off('error',                 handleError);
            socket.off('error:wrongPassword',   handleWrongPassword);
            socket.off('error:alreadyLoggedIn', handleAlreadyLoggedIn);
            socket.off('room:join:response',    handleRoomJoinResponse);
            socket.off('player:active-session', handleActiveSession);
            if (joinFallbackTimerRef.current) clearTimeout(joinFallbackTimerRef.current);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket, roomId]);

    useEffect(() => {
        if (!roomId) return;

        let cancelled = false;
        axios.get(`${backendUrl}/api/join-room/${roomId}/meta`)
            .then(({ data }) => {
                if (cancelled) return;
                setRoomMeta(data?.room || null);
                if (data?.status === 'not_found') {
                    setError(data.message || 'Room not found');
                }
            })
            .catch(err => {
                if (!cancelled && err.response?.status >= 500) {
                    setError(err.response?.data?.message || 'Could not load room information');
                }
            });

        return () => { cancelled = true; };
    }, [roomId]);

    useEffect(() => {
        if (!roomId) return;

        const searchParams = new URLSearchParams(location.search);
        const tokenParam = searchParams.get('token');

        let cancelled = false;
        let storedSession = {};
        try {
            const saved = sessionKey ? localStorage.getItem(sessionKey) : null;
            storedSession = saved ? JSON.parse(saved) : {};
        } catch (_) {}

        const applySession = session => {
            const resolvedToken = tokenParam || session.token || '';
            const resolvedPassword = session.password || '';
            const resolvedName = session.name || localStorage.getItem('playerName') || '';
            const resolvedMobile = session.mobile || '';
            const resolvedEmail = session.email || '';

            setJoinToken(resolvedToken);
            setPassword(resolvedPassword);
            setName(resolvedName);
            setMobile(resolvedMobile);
            setEmail(resolvedEmail);
            setNameLocked(Boolean(resolvedToken && resolvedName));
        };

        if (!tokenParam) {
            applySession(storedSession);
            return;
        }

        setSlotLoading(true);
        axios.get(`${backendUrl}/api/join-slot/${roomId}`, { params: { token: tokenParam } })
            .then(({ data }) => {
                if (cancelled) return;
                const player = data?.player || {};
                const session = {
                    token: tokenParam,
                    name: player.name || '',
                    mobile: player.mobile || '',
                    email: player.email || '',
                    password: '',
                };
                try { if (sessionKey) localStorage.setItem(sessionKey, JSON.stringify(session)); } catch (_) {}
                setJoinToken(tokenParam);
                setName(session.name);
                setMobile(session.mobile);
                setEmail(session.email);
                setPassword('');
                setNameLocked(Boolean(session.name));

                if (session.name && socket && !autoJoinFiredRef.current && !blockingSession) {
                    autoJoinFiredRef.current = true;
                    emitJoin(session.name, '', tokenParam, session.mobile, session.email);
                }
            })
            .catch(err => {
                if (cancelled) return;
                const msg = err.response?.data?.message || 'Invalid join link';
                setError(msg);
                toastManager.info(msg);
                applySession(storedSession);
            })
            .finally(() => {
                if (!cancelled) setSlotLoading(false);
            });

        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [roomId, location.search, socket, blockingSession]);

    const handleJoinGame = (e) => {
        e.preventDefault();
        if (!name.trim()) { setError('Please enter your name'); toastManager.info('Please enter your name'); return; }
        if (!socket) { setError('No connection to server.'); return; }
        if (blockingSession) {
            setError('Finish the old game choice first.');
            return;
        }
        autoJoinFiredRef.current = true;
        emitJoin(name, password, joinToken, mobile, email);
    };

    const handleResumeOldGame = () => {
        if (!socket || !blockingSession?.roomId || !blockingSession?.playerId) return;
        socket.emit(
            'player:restore-session',
            {
                roomId: blockingSession.roomId,
                playerId: blockingSession.playerId,
                color: blockingSession.color,
                seat: blockingSession.seat,
            },
            response => {
                if (response?.success) {
                    socket.timeout(10000).emit('player:reconnect', blockingSession.roomId, (timeoutError, reconnectResponse) => {
                        if (timeoutError || !reconnectResponse?.success) {
                            setError(reconnectResponse?.message || 'Could not resume old game.');
                            return;
                        }
                        navigate('/play');
                    });
                } else {
                    setError(response?.message || 'Could not resume old game.');
                }
            }
        );
    };

    const handleLeaveOldAndJoinThis = () => {
        if (!socket) return;
        socket.emit('player:abandon-game');
        setBlockingSession(null);
        try {
            localStorage.removeItem('ludoActiveSession');
            localStorage.removeItem('ludoLastGameResult');
        } catch (_) {}
    };

    // ── Auto-join is in progress — show only a nice "Joining as [name]" screen
    if (slotLoading || loading || (nameLocked && joinToken)) {
        return (
            <div className={styles.container}>
                <div className={styles.joinForm} style={{ textAlign: 'center' }}>
                    <div style={{ fontSize: 48, marginBottom: 12 }}>🎲</div>
                    <h2 style={{ color: '#ecf0f1', marginBottom: 6 }}>Joining as</h2>
                    <div style={{ fontSize: 22, fontWeight: 800, color: '#3498db', marginBottom: 20 }}>{name}</div>
                    {error ? (
                        <div className={styles.error}>{error}</div>
                    ) : (
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                            <div className={styles.loadingSpinner} style={{ position: 'static' }} />
                            <p style={{ color: '#bdc3c7', fontSize: 13 }}>{slotLoading ? 'Loading player data…' : 'Connecting to game…'}</p>
                        </div>
                    )}
                    {error && (
                        <button onClick={() => navigate('/')} className={styles.backButton} style={{ marginTop: 16, width: '100%' }}>
                            Go Home
                        </button>
                    )}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.joinForm}>
                <h2>Join Game</h2>
                <div style={{
                    marginBottom: 18,
                    padding: '14px 16px',
                    borderRadius: 16,
                    background: 'rgba(255,255,255,0.04)',
                    border: '1px solid rgba(255,255,255,0.08)',
                }}>
                    <div style={{ fontSize: 18, fontWeight: 800, color: '#ecf0f1' }}>{roomMeta?.name || 'Game Room'}</div>
                    <div style={{ marginTop: 8, display: 'grid', gap: 6 }}>
                        <div className={styles.roomInfo}>Room ID: {roomId}</div>
                        {roomMeta?.roomCode && <div className={styles.roomInfo}>Room Code: {roomMeta.roomCode}</div>}
                        <div className={styles.roomInfo}>Players: {roomMeta?.maxPlayers || 4}</div>
                    </div>
                </div>

                {error && <div className={styles.error}>{error}</div>}

                {blockingSession && (
                    <div style={{
                        marginBottom: 18,
                        padding: '14px 16px',
                        borderRadius: 16,
                        background: 'rgba(255, 193, 7, 0.1)',
                        border: '1px solid rgba(255, 193, 7, 0.35)',
                        color: '#f9e79f',
                    }}>
                        <div style={{ fontSize: 18, fontWeight: 800, marginBottom: 8 }}>Old game found</div>
                        <div style={{ fontSize: 13, lineHeight: 1.5, marginBottom: 14 }}>
                            You already have an unfinished game in <strong>{blockingSession.roomName || 'another room'}</strong>.
                            Choose whether to rejoin that game or leave it and join this room.
                        </div>
                        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                            <button type="button" className={styles.joinButton} onClick={handleResumeOldGame}>
                                Join Old Game
                            </button>
                            <button
                                type="button"
                                className={styles.backButton}
                                onClick={handleLeaveOldAndJoinThis}
                                style={{ background: '#8e2a2a', borderColor: '#c0392b' }}
                            >
                                Leave Old And Join This
                            </button>
                        </div>
                    </div>
                )}

                <form onSubmit={handleJoinGame}>
                    <div className={styles.formGroup}>
                        <label htmlFor="name">Your Name</label>
                        <input
                            type="text"
                            id="name"
                            value={name}
                            onChange={nameLocked ? undefined : (e) => setName(e.target.value)}
                            readOnly={nameLocked}
                            placeholder="Enter your name"
                            required
                            style={nameLocked ? { opacity: 0.7, cursor: 'not-allowed', background: '#2c3e50' } : {}}
                        />
                        {nameLocked && <div style={{ fontSize: 11, color: '#7f8c8d', marginTop: 4 }}>Name set by room creator</div>}
                    </div>

                    {!joinToken && (
                        <div className={styles.formGroup}>
                            <label htmlFor="password">Room Password (if required)</label>
                            <PasswordField
                                id="password"
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                placeholder="Enter room password if needed"
                            />
                        </div>
                    )}

                    <div className={styles.buttons}>
                        <button type="button" onClick={() => navigate('/')} className={styles.backButton} disabled={loading}>
                            Back
                        </button>
                        <button type="submit" className={styles.joinButton} disabled={loading || Boolean(blockingSession)}>
                            {loading ? 'Joining...' : 'Join Game'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default GameJoinPage;
